import { toFixed } from 'accounting-js';
import getCouponDiscountAmount from './getCouponDiscountAmount';
import { checkIsOrderCoupon } from './checkCoupon';
import checkHasValidStartDate from './checkHasValidStartDate';
import checkHasTrialPeriod from './checkHasTrialPeriod';

const amountInCents = amount => Number(toFixed(amount, 0));

/**
 * @description Calculates the discount amounts split across recurring, one-time, and setup fee.
 *
 * For amountOff coupons with a start date or trial (recurring deferred to future invoice):
 *   - First invoice only has one-time variants + setup fee
 *   - Discount is capped at first-invoice discountable items
 *   - Recurring discount is applied independently on future invoices
 *   This matches Stripe's per-invoice behavior.
 *
 * For all other cases (percentOff, or amountOff with no start date):
 *   - Discount is split proportionally across all discountable items
 *   This matches Stripe's proportional distribution within a single invoice.
 */
export function getProportionateDiscountAmounts({
  recurringSubtotalAmount = 0,
  oneTimeSubtotalAmount = 0,
  setupFeeAmount = 0,
  ticketFeesAmount = 0,
  coupon,
  trialPeriodDays = 0,
  startDate = null,
  pageId,
  pageTicketTypes = [],
  primary,
  purchase,
}) {
  if (!coupon || !checkIsOrderCoupon(coupon, pageId, pageTicketTypes, primary, purchase)) {
    return {
      recurringDiscountAmount: 0,
      oneTimeVariantsDiscountAmount: 0,
      setupFeeDiscountAmount: 0,
      couponDiscountAmount: 0,
    };
  }

  const hasValidStartDate = checkHasValidStartDate(startDate);
  const hasTrialPeriod = checkHasTrialPeriod(trialPeriodDays);
  const isRecurringDeferred = hasValidStartDate || hasTrialPeriod;

  // For amountOff coupons with deferred recurring, the first invoice only contains
  // one-time items. Stripe applies the coupon per-invoice, so we calculate the
  // first-invoice discount separately from the future recurring discount.
  if (coupon.amountOff && isRecurringDeferred) {
    // First invoice discountable items: one-time variants + setup fee (if applicable)
    let firstInvoiceDiscountable = oneTimeSubtotalAmount;
    if (coupon.appliesToSetupFee && setupFeeAmount > 0) {
      firstInvoiceDiscountable += setupFeeAmount;
    }

    // First invoice discount, capped at discountable items on that invoice
    const firstInvoiceDiscount = Math.min(coupon.amountOff, firstInvoiceDiscountable);

    // Distribute proportionally among first-invoice discountable items
    let oneTimeVariantsDiscountAmount = 0;
    let setupFeeDiscountAmount = 0;
    if (firstInvoiceDiscountable > 0) {
      oneTimeVariantsDiscountAmount =
        firstInvoiceDiscount * (oneTimeSubtotalAmount / firstInvoiceDiscountable);
      if (coupon.appliesToSetupFee && setupFeeAmount > 0) {
        setupFeeDiscountAmount = firstInvoiceDiscount * (setupFeeAmount / firstInvoiceDiscountable);
      }
    }

    // Future recurring invoices: full coupon reapplied independently each billing cycle
    const recurringDiscountAmount = Math.min(coupon.amountOff, recurringSubtotalAmount);

    return {
      recurringDiscountAmount: amountInCents(recurringDiscountAmount),
      oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
      setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
      couponDiscountAmount: amountInCents(firstInvoiceDiscount),
    };
  }

  // For percentOff coupons with deferred recurring + setup fee:
  // special case to prevent incorrect proportions
  if (
    recurringSubtotalAmount > 0 &&
    isRecurringDeferred &&
    setupFeeAmount > 0 &&
    coupon.appliesToSetupFee
  ) {
    const couponDiscountAmount = getCouponDiscountAmount({
      amount: oneTimeSubtotalAmount,
      setupFeeAmount,
      coupon,
      pageId,
      pageTicketTypes,
      primary,
      purchase,
    });

    let totalAmount = oneTimeSubtotalAmount + setupFeeAmount;

    let oneTimeProportion = 0;
    let setupFeeProportion = 0;
    if (totalAmount > 0) {
      oneTimeProportion = oneTimeSubtotalAmount / totalAmount;
      if (coupon.appliesToSetupFee) {
        setupFeeProportion = setupFeeAmount / totalAmount;
      }
    }

    const oneTimeVariantsDiscountAmount = couponDiscountAmount * oneTimeProportion;
    const setupFeeDiscountAmount = coupon.appliesToSetupFee
      ? couponDiscountAmount * setupFeeProportion
      : 0;

    if (coupon.duration === 'once') {
      return {
        recurringDiscountAmount: 0,
        oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
        setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
        couponDiscountAmount: amountInCents(couponDiscountAmount),
      };
    } else {
      return {
        recurringDiscountAmount: amountInCents(couponDiscountAmount),
        oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
        setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
        couponDiscountAmount: amountInCents(couponDiscountAmount),
      };
    }
  }

  // Default: proportional split across all discountable items (matches Stripe within a single invoice)
  // Ticket fees are an event-only base widening: events have no recurring or
  // setup-fee money, so the fee share needs no apportioning of its own.
  const couponDiscountAmount = getCouponDiscountAmount({
    amount: recurringSubtotalAmount + oneTimeSubtotalAmount,
    setupFeeAmount,
    ticketFeesAmount,
    coupon,
    pageId,
    pageTicketTypes,
    primary,
    purchase,
  });

  let totalAmount = recurringSubtotalAmount + oneTimeSubtotalAmount;
  if (coupon && coupon.appliesToSetupFee) {
    totalAmount += setupFeeAmount;
  }

  let recurringProportion = 0;
  let oneTimeProportion = 0;
  let setupFeeProportion = 0;
  if (totalAmount > 0) {
    recurringProportion = recurringSubtotalAmount / totalAmount;
    oneTimeProportion = oneTimeSubtotalAmount / totalAmount;
    if (coupon && coupon.appliesToSetupFee) {
      setupFeeProportion = setupFeeAmount / totalAmount;
    }
  }

  // Capped as the API's split is; the recurring share keeps the leftover Stripe takes off
  // recurring bumps and fees.
  const recurringDiscountAmount = couponDiscountAmount * recurringProportion;
  const oneTimeVariantsDiscountAmount = Math.min(
    couponDiscountAmount * oneTimeProportion,
    oneTimeSubtotalAmount,
  );
  const setupFeeDiscountAmount = coupon.appliesToSetupFee
    ? Math.min(couponDiscountAmount * setupFeeProportion, setupFeeAmount)
    : 0;

  return {
    recurringDiscountAmount: amountInCents(recurringDiscountAmount),
    oneTimeVariantsDiscountAmount: amountInCents(oneTimeVariantsDiscountAmount),
    setupFeeDiscountAmount: amountInCents(setupFeeDiscountAmount),
    couponDiscountAmount: amountInCents(couponDiscountAmount),
  };
}
